import { useEffect, useState } from "react";
import { FileText, Download, Eye, Send, Printer, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, blobUrl, download, errMsg, fmtTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Badge, EmptyState, useConfirm } from "@/components/opl/Common";
import { useList, GoldBtn, IconBtn } from "@/components/opl/Crud";

const STATUS = { draft: "text-orange-300 border-orange-500/40", published: "text-emerald-300 border-emerald-500/40", archived: "text-slate-400" };

export default function Rulebook() {
  const { can } = useAuth();
  const [rows, load] = useList("/rulebooks");
  const [preview, setPreview] = useState(null);
  const [dialog, confirm] = useConfirm();
  useEffect(() => () => preview && URL.revokeObjectURL(preview.url), [preview]);
  const show = async (r) => {
    try { setPreview({ id: r.id, url: await blobUrl(`/rulebooks/${r.id}/download?inline=true`) }); } catch (e) { toast.error(errMsg(e)); }
  };
  const gen = async () => {
    try { const r = await api.post("/rulebooks/generate"); toast.success(`Draft v${r.data.version} generated`); load(); show(r.data); } catch (e) { toast.error(errMsg(e)); }
  };
  const print = () => { const w = window.open(preview.url); w && setTimeout(() => w.print(), 800); };
  return (
    <div>
      {dialog}
      <PageHeader title="Rulebook Generator" crumbs={["Rulebook"]} eyebrow="Official PDF">
        <GoldBtn testid="generate-rulebook-btn" onClick={gen}><RefreshCw className="h-4 w-4" />Generate new draft</GoldBtn>
      </PageHeader>
      <p className="text-sm text-slate-400 mb-6">The PDF is built from <b>published rules</b>, categories and the auction configuration stored in the database. Drafts carry a DRAFT watermark until published. Older published versions are archived.</p>
      <div className="grid xl:grid-cols-5 gap-6">
        <div className="xl:col-span-2 space-y-2" data-testid="rulebook-versions">
          {rows?.map((r) => (
            <div key={r.id} className={`surface rounded-xl p-4 flex items-center gap-3 ${preview?.id === r.id ? "border-[#E5B842]/50" : ""}`} data-testid={`rulebook-v${r.version}`}>
              <FileText className="h-5 w-5 text-[#E5B842]" />
              <div className="flex-1"><div className="font-bold">Version {r.version} <Badge className={STATUS[r.status]}>{r.status}</Badge></div><div className="text-xs text-slate-500">{fmtTime(r.published_at || r.created_at)} · {r.rules_count} rules · {r.created_by}</div></div>
              <IconBtn title="Preview" testid={`preview-rulebook-${r.version}`} onClick={() => show(r)}><Eye className="h-4 w-4" /></IconBtn>
              <IconBtn title="Download" testid={`download-rulebook-${r.version}`} onClick={() => download(`/rulebooks/${r.id}/download`, `OPL_S6_Rulebook_v${r.version}.pdf`)}><Download className="h-4 w-4" /></IconBtn>
              {can("publish_rules") && r.status === "draft" && <IconBtn title="Publish" testid={`publish-rulebook-${r.version}`} onClick={() => confirm({ title: `Publish rulebook v${r.version}?`, description: "The watermark is removed and this becomes the official public download.", confirmLabel: "Publish", onConfirm: async () => { await api.post(`/rulebooks/${r.id}/publish`); toast.success("Rulebook published"); load(); } })}><Send className="h-4 w-4" /></IconBtn>}
            </div>
          ))}
          {rows && !rows.length && <EmptyState title="No rulebook generated yet" hint="Publish rules first, then generate a draft." />}
        </div>
        <div className="xl:col-span-3 surface rounded-2xl p-3 min-h-[600px]" data-testid="rulebook-preview">
          {preview ? <>
            <div className="flex justify-end mb-2"><IconBtn testid="print-rulebook-btn" title="Print" onClick={print}><Printer className="h-4 w-4" /></IconBtn></div>
            <iframe title="Rulebook preview" src={preview.url} className="w-full h-[75vh] rounded-lg bg-white" />
          </> : <div className="h-full grid place-items-center text-slate-500">Select a version to preview</div>}
        </div>
      </div>
    </div>
  );
}
